import React from 'react';
import { FieldListContainerProps, FieldListViewProps } from './fieldListProps';
import { ReportingContainerBaseConfig } from '../../../../base';
import { FieldListController, FieldListLoader } from './fieldListInterface';

/**
 * Generate a local ID for new form fields/options
 * These are used for form state management and should not be sent to the server
 */
function generateLocalId(): string {
    return `_local_${crypto.randomUUID()}`;
}

export function createFieldListContainer(
    config: ReportingContainerBaseConfig,
    loader: FieldListLoader,
    controller: FieldListController,
    View: React.FC<FieldListViewProps>
): React.FC<FieldListContainerProps> {
    const {
        context: { useContext },
    } = config;

    return (containerProps) => {
        const context = useContext();
        const data = loader.useLoad(context, {
            form: {
                id: containerProps.form.id,
            },
        });

        const props = controller.useProps(context, data, {
            values: containerProps.value,
            onAdd(field, index) {
                const currentFields = containerProps.value.fields;
                const newFields = [...currentFields];
                const fieldWithId = {
                    ...field,
                    _local_id: field._local_id || generateLocalId(),
                    id: undefined,
                    type: field.type || 'string',
                    required: field.required ?? true,
                    description: field.description ?? null,
                    showDescription: field.showDescription ?? false,
                };
                newFields.splice(index, 0, fieldWithId);
                containerProps.onChange({
                    fields: newFields,
                });
            },
            onUpdate(field) {
                const updatedFields = containerProps.value.fields.map((candidate) =>
                    candidate._local_id === field._local_id ? field : candidate
                );
                containerProps.onChange({
                    fields: updatedFields,
                });
            },
            onRemove(field) {
                const filteredFields = containerProps.value.fields.filter(
                    (candidate) => candidate._local_id !== field._local_id
                );
                containerProps.onChange({
                    fields: filteredFields,
                });
            },
            onReorder(field, toIndex) {
                const currentFields = containerProps.value.fields;
                const fromIndex = currentFields.findIndex(
                    (f) => f._local_id === field._local_id
                );

                if (fromIndex === -1 || fromIndex === toIndex) return;

                const newFields = [...currentFields];
                const [removedField] = newFields.splice(fromIndex, 1);
                newFields.splice(toIndex, 0, removedField);

                containerProps.onChange({
                    fields: newFields,
                });
            },
        });
        return <View {...props} />;
    };
}
